import { ref, computed, readonly, Ref, watch, ComputedRef } from 'vue';
import { useAuthentication } from '@/composables/useAuthentication';
import { useModes } from '@/composables/useModes';
import { useAccountLevelsMaximumLimits } from '@/composables/useAccountLevelsMaximumLimits';
import { isValidNumber } from '@/utils/isValidNumber';
import { isValidSharpCharts2DateString } from '@/utils/isValidSharpCharts2DateString';
import { isValidTimeString } from '@/utils/isValidTimeString';
import { LoadingState } from '@/enums/loading-state';
import { JsonLayerResponse, isJsonLayerResponse } from '@/interfaces/jsonLayerResponse';

interface JsonSummaryAdvancedAlert {
  alertpaused: 'yes' | 'no';
  alert: string;
  type: 'a',
  recid: string;
  lastfired?: string;
  notes?: string;
};

function isJsonSummaryAdvancedAlert(unknownValue: unknown): unknownValue is JsonSummaryAdvancedAlert {
  return (
    typeof unknownValue === 'object' &&
    unknownValue !== null &&
    'alertpaused' in unknownValue &&
    'alert' in unknownValue &&
    'type' in unknownValue &&
    'recid' in unknownValue &&
    (unknownValue.alertpaused === 'yes' || unknownValue.alertpaused === 'no') &&
    typeof unknownValue.alert === 'string' &&
    unknownValue.type === 'a' &&
    typeof unknownValue.recid === 'string' &&
    (!('lastfired' in unknownValue) || typeof unknownValue.lastfired === 'string') &&
    (!('notes' in unknownValue) || typeof unknownValue.notes === 'string')
  );
}

interface JsonSummaryPriceAlert {
  symbol: string;
  alertpaused: 'yes' | 'no';
  alert: string;
  price: string;
  type: 'p',
  recid: string;
  lastfired?: string;
  notes?: string;
};

function isJsonSummaryPriceAlert(unknownValue: unknown): unknownValue is JsonSummaryPriceAlert {
  return (
    typeof unknownValue === 'object' &&
    unknownValue !== null &&
    'symbol' in unknownValue &&
    'alertpaused' in unknownValue &&
    'alert' in unknownValue &&
    'price' in unknownValue &&
    'type' in unknownValue &&
    'recid' in unknownValue &&
    typeof unknownValue.symbol === 'string' &&
    (unknownValue.alertpaused === 'yes' || unknownValue.alertpaused === 'no') &&
    typeof unknownValue.alert === 'string' &&
    typeof unknownValue.price === 'string' &&
    unknownValue.type === 'p' &&
    typeof unknownValue.recid === 'string' &&
    (!('lastfired' in unknownValue) || typeof unknownValue.lastfired === 'string') &&
    (!('notes' in unknownValue) || typeof unknownValue.notes === 'string')
  );
}

interface JsonLayerPredefinedAlert {
  symbol: string;
  alertName: string;
  lastAlert: string;
  recID: number;
  isBearish: boolean;
};

function isJsonLayerPredefinedAlert(unknownValue: unknown): unknownValue is JsonLayerPredefinedAlert {
  return (
    typeof unknownValue === 'object' &&
    unknownValue !== null &&
    'symbol' in unknownValue &&
    'alertName' in unknownValue &&
    'lastAlert' in unknownValue &&
    'recID' in unknownValue &&
    'isBearish' in unknownValue &&
    typeof unknownValue.symbol === 'string' &&
    typeof unknownValue.alertName === 'string' &&
    typeof unknownValue.lastAlert === 'string' &&
    typeof unknownValue.recID === 'number' &&
    typeof unknownValue.isBearish === 'boolean'
  );
}

export interface AdvancedAlert {
  name: string;
  lastFiredDateString: string;
  recid: number;
  isPaused: boolean;
  status: string;
  notes: string;
  lastFiredDateObject: Date | null;
};

export interface PriceAlert {
  name: string;
  lastFiredDateString: string;
  recid: number;
  symbol: string;
  isPaused: boolean;
  status: string;
  notes: string;
  lastFiredDateObject: Date | null;
  price: string;
  delta: string;
};

interface PredefinedAlert {
  name: string;
  lastFiredDateString: string;
  recid: number;
  symbol: string;
  isBearish: boolean;
};

const CHECK_INTERVAL = 1 * 1000;
const REFRESH_INTERVAL = 15 * 1000;

const advancedAlerts: Ref<AdvancedAlert[] | null> = ref(null);
const priceAlerts: Ref<PriceAlert[] | null> = ref(null);
const predefinedAlerts: Ref<PredefinedAlert[] | null> = ref(null);
const advancedAlertsAndPriceAlertsLoadingState = ref(LoadingState.PENDING);
const predefinedAlertsLoadingState = ref(LoadingState.PENDING);
let lastAdvancedAlertAndPriceAlertRefreshTime = -1;
let lastPredefinedAlertRefreshTime = -1;
let isInitialized = false;
let isPredefinedAlertsModeOpen: ComputedRef<boolean> | undefined;
let isPriceAlertsModeOpen: ComputedRef<boolean> | undefined;
let isAdvancedAlertsModeOpen: ComputedRef<boolean> | undefined;

const currentNumberOfPriceAlerts = computed(() => {
  return priceAlerts.value?.length ?? 0;
});

const hasReachedMaximumNumberOfPriceAlerts = computed(() => {
  const { isAccountLevelsMaximumLimitsInitialized, maximumNumberOfPriceAlertsAllowed } = useAccountLevelsMaximumLimits();

  return (
    isAccountLevelsMaximumLimitsInitialized.value &&
    currentNumberOfPriceAlerts.value >= maximumNumberOfPriceAlertsAllowed.value &&
    maximumNumberOfPriceAlertsAllowed.value > 0
  );
});

async function createPriceAlert(
  symbol: string,
  price: string,
  isPriceAlertDirectionCrossesAbove: boolean,
  message: string,
  isEmailNotification: boolean,
  isTextMessageNotification: boolean,
  isSiteNotification: boolean
): Promise<void> {
  const { isLoggedIn, chartCode } = useAuthentication();

  if (!isLoggedIn.value) {
    throw new Error('Unable to create price alert - user is not logged in.');
  }

  let notificationCode = 0;

  if (isEmailNotification) {
    notificationCode += 1;
  }

  if (isTextMessageNotification) {
    notificationCode += 2;
  }

  if (isSiteNotification) {
    notificationCode += 4;
  }

  if (!notificationCode) {
    throw new Error('Unable to create price alert - no notification type (email, text, or site notification) was selected.');
  }

  const chartCodeCopy = chartCode.value;
  const alertDirection = isPriceAlertDirectionCrossesAbove ? 'U' : 'D';
  let jsonLayerResponse;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: 'add-pricealert',
        chartCode: chartCodeCopy,
        symbol,
        alertPrice: price,
        alertDirection,
        notes: message,
        notificationType: notificationCode.toString()
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    jsonLayerResponse = await response.json();

    window.sc3WorkbenchLogger.info('create price alert jsonLayerResponse', jsonLayerResponse);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to create price alert for chartCode: ${chartCodeCopy} and symbol: ${symbol} and price: ${price} and alertDirection: ${alertDirection}, and notificationType: ${notificationCode} and notes: ${message}.`, e);

    throw new Error(`Failed to create price alert for chartCode: ${chartCodeCopy} and symbol: ${symbol} and alertPrice: ${price} and alertDirection: ${alertDirection}, and notificationType: ${notificationCode} and notes: ${message}.`, { cause: e });
  }

  if (!isJsonLayerResponse(jsonLayerResponse)) {
    throw new Error(`Failed to create price alert for chartCode: ${chartCodeCopy} and symbol: ${symbol} and alertPrice: ${price} and alertDirection: ${alertDirection}, and notificationType: ${notificationCode} and notes: ${message} - jsonLayerResponse does not match JsonLayerResponse interface.`);
  }

  if (!jsonLayerResponse.success) {
    throw new Error(`Failed to create price alert for chartCode: ${chartCodeCopy} and symbol: ${symbol} and alertPrice: ${price} and alertDirection: ${alertDirection}, and notificationType: ${notificationCode} and notes: ${message} - jsonLayerResponse.success is false - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }
}

async function fetchAdvancedAlertsAndPriceAlerts(): Promise<{ jsonSummaryAdvancedAlerts: JsonSummaryAdvancedAlert[], jsonSummaryPriceAlerts: JsonSummaryPriceAlert[] }> {
  const { isLoggedIn } = useAuthentication();

  if (!isLoggedIn.value) {
    throw new Error('Unable to fetch advanced alerts and price alerts - user is not logged in.');
  }

  const urlSearchParams = new URLSearchParams({
    cmd: 'alert',
    view: 'U',
    randomNumber: Date.now().toString()
  });

  let jsonSummaryResponse;

  try {
    const response = await fetch(`/j-sum/sum?${urlSearchParams.toString()}`, {
      method: 'GET'
    });

    jsonSummaryResponse = await response.json();

    window.sc3WorkbenchLogger.info(`fetch advanced alerts and price alerts jsonSummaryResponse.`, jsonSummaryResponse);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to fetch advanced alerts and price alerts.`, e);

    throw new Error(`Failed to fetch advanced alerts and price alerts.`, { cause: e });
  }

  if (!jsonSummaryResponse) {
    throw new Error(`Failed to fetch advanced alerts and price alerts - jsonSummaryResponse is falsy: ${typeof jsonSummaryResponse} ${jsonSummaryResponse}.`);
  }

  if (typeof jsonSummaryResponse !== 'object') {
    throw new Error(`Failed to fetch advanced alerts and price alerts - jsonSummaryResponse is truthy but is not an object: ${typeof jsonSummaryResponse}.`);
  }

  if (!Array.isArray(jsonSummaryResponse)) {
    throw new Error(`Failed to fetch advanced alerts and price alerts - jsonSummaryResponse is a truthy object but is not an array.`);
  }

  // we expect json-summary to add 1 object to the beginning of the jsonSummaryResponse array that's different from all the other
  // objects that come after: this object is empty except for 1 'alertcount' property. we want to filter this object out.
  // this should leave us with an array of objects that are all either advanced alerts or price alerts.
  const unknownValues: unknown[] = jsonSummaryResponse.filter((unknownValue: unknown) => {
    return (
      typeof unknownValue !== 'object' ||
      unknownValue === null ||
      !('alertcount' in unknownValue)
    );
  });

  const numFilteredOutAlertcountObjects = jsonSummaryResponse.length - unknownValues.length;

  if (numFilteredOutAlertcountObjects > 1) {
    window.sc3WorkbenchLogger.warn(`json-summary cmd=alert&view=U response array contained ${numFilteredOutAlertcountObjects} objects that contained 'alertcount' properties, which is more than the 1 object expected. These objects have been removed and ignored.`);
  }

  const jsonSummaryAdvancedAlerts = unknownValues.filter(isJsonSummaryAdvancedAlert);
  const jsonSummaryPriceAlerts = unknownValues.filter(isJsonSummaryPriceAlert);
  const numInvalidJsonSummaryAlerts = unknownValues.length - (jsonSummaryAdvancedAlerts.length + jsonSummaryPriceAlerts.length);

  if (numInvalidJsonSummaryAlerts > 0) {
    window.sc3WorkbenchLogger.warn(`json-summary cmd=alert&view=U response contained ${numInvalidJsonSummaryAlerts} invalid alerts (which did not match the JsonSummaryAdvancedAlert interface or JsonSummaryPriceAlert interface).`);
  }

  return { jsonSummaryAdvancedAlerts, jsonSummaryPriceAlerts };
}

async function loadAdvancedAlertsAndPriceAlerts(): Promise<void> {
  const { isLoggedIn } = useAuthentication();

  if (!isLoggedIn.value || advancedAlertsAndPriceAlertsLoadingState.value === LoadingState.LOADING) {
    return;
  }

  advancedAlertsAndPriceAlertsLoadingState.value = LoadingState.LOADING;

  try {
    const { jsonSummaryAdvancedAlerts, jsonSummaryPriceAlerts }  = await fetchAdvancedAlertsAndPriceAlerts();

    advancedAlerts.value = jsonSummaryAdvancedAlerts
      .map(parseAdvancedAlert)
      .filter(advancedAlertOrNull => !!advancedAlertOrNull);


    priceAlerts.value = jsonSummaryPriceAlerts
      .map(parsePriceAlert)
      .filter(priceAlertOrNull => !!priceAlertOrNull);

    advancedAlertsAndPriceAlertsLoadingState.value = LoadingState.LOADED;
  } catch (e) {
    window.sc3WorkbenchLogger.error('Error occurred while loading advanced alerts and price alerts.', e);

    advancedAlertsAndPriceAlertsLoadingState.value = LoadingState.ERROR;
  }

  lastAdvancedAlertAndPriceAlertRefreshTime = Date.now();
}

interface FetchPredefinedAlertsResponse extends JsonLayerResponse {
  predefinedAlerts: any[];
};

function isFetchPredefinedAlertsResponse(jsonLayerResponse: JsonLayerResponse): jsonLayerResponse is FetchPredefinedAlertsResponse {
  return (
    'predefinedAlerts' in jsonLayerResponse &&
    Array.isArray(jsonLayerResponse.predefinedAlerts)
  );
}

async function fetchPredefinedAlerts(): Promise<JsonLayerPredefinedAlert[]> {
  const { isLoggedIn } = useAuthentication();

  if (!isLoggedIn.value) {
    throw new Error('Unable to fetch predefined alerts - user is not logged in.');
  }

  let jsonLayerResponse;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: 'get-all-predefined-alerts'
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    jsonLayerResponse = await response.json();

    window.sc3WorkbenchLogger.info(`fetch predefined alerts jsonLayerResponse.`, jsonLayerResponse);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to fetch predefined alerts.`, e);

    throw new Error(`Failed to fetch predefined alerts.`, { cause: e });
  }

  if (!isJsonLayerResponse(jsonLayerResponse)) {
    throw new Error(`Failed to fetch predefined alerts - jsonLayerResponse does not match JsonLayerResponse interface.`);
  }

  if (!jsonLayerResponse.success) {
    throw new Error(`Failed to fetch predefined alerts - jsonLayerResponse.success is false - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }

  if (!isFetchPredefinedAlertsResponse(jsonLayerResponse)) {
    throw new Error(`Failed to fetch predefined alerts - jsonLayerResponse.success is true but jsonLayerResponse does not match FetchPredefinedAlertsResponse interface - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }

  const jsonLayerPredefinedAlerts = jsonLayerResponse.predefinedAlerts.filter(isJsonLayerPredefinedAlert);
  const numInvalidJsonLayerPredefinedAlerts = jsonLayerResponse.predefinedAlerts.length - jsonLayerPredefinedAlerts.length;

  if (numInvalidJsonLayerPredefinedAlerts > 0) {
    window.sc3WorkbenchLogger.warn(`json-layer get-all-predefined-alerts command returned ${numInvalidJsonLayerPredefinedAlerts} invalid predefined alerts (which did not match the JsonLayerPredefinedAlert interface).`);
  }

  return jsonLayerPredefinedAlerts;
}

async function loadPredefinedAlerts(): Promise<void> {
  const { isLoggedIn } = useAuthentication();

  if (!isLoggedIn.value || predefinedAlertsLoadingState.value === LoadingState.LOADING) {
    return;
  }

  predefinedAlertsLoadingState.value = LoadingState.LOADING;

  try {
    const jsonLayerPredefinedAlerts = await fetchPredefinedAlerts();

    predefinedAlerts.value = jsonLayerPredefinedAlerts
      .map(parsePredefinedAlert)
      .filter(predefinedAlertOrNull => !!predefinedAlertOrNull);

    predefinedAlertsLoadingState.value = LoadingState.LOADED;
  } catch (e) {
    window.sc3WorkbenchLogger.error('Error occurred while loading predefined alerts.', e);

    predefinedAlertsLoadingState.value = LoadingState.ERROR;
  }

  lastPredefinedAlertRefreshTime = Date.now();
}

function parseJsonSummaryAdvancedAlertOrPriceAlertDate(dateString: string): Date | null {
  const dateStringParts = dateString
    .match(/^([0-9]{1,2}) ([A-Za-z]{3}) ([0-9]{4}), ([0-9]{1,2}):([0-9]{2})(am|pm)$/i) // '29 Jul 2022 10:24am' -> ['29 Jul 2022 10:24am', '29', 'Jul', '2022', '10', '24', 'am']
    ?.slice(1); // remove the first match in the array, which is the full input string (i.e. '29 Jul 2022 10:24am'), which we don't care about

  if (!dateStringParts) {
    return null;
  }

  const [dayString, monthString, yearString, hourString, minuteString, amPmString] = dateStringParts;
  const day = Number(dayString);
  const month = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'].indexOf(monthString.toLowerCase());
  const year = Number(yearString);

  if (!isValidSharpCharts2DateString(`${year}-${month < 9 ? '0' : ''}${month + 1}-${day < 10 ? '0' : ''}${day}`)) {
    return null;
  }

  let hour = Number(hourString);

  if (hour < 1 || hour > 12) {
    return null;
  }

  if (amPmString.toLowerCase() === 'pm' && hour !== 12) {
    hour += 12;
  } else if (amPmString.toLowerCase() === 'am' && hour === 12) {
    hour = 0;
  }

  const minute = Number(minuteString);

  if (!isValidTimeString(`${hour < 10 ? '0' : ''}${hour}:${minute < 10 ? '0' : ''}${minute}:00`)) {
    return null;
  }

  return new Date(year, month, day, hour, minute);
}

function formatAlertDateString(date: Date): string {
  const dateTimeFormatOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZone: 'America/New_York'
  };

  return new Intl.DateTimeFormat('en-US', dateTimeFormatOptions).format(date);
}

function parseAdvancedAlert(jsonSummaryAdvancedAlert: JsonSummaryAdvancedAlert): AdvancedAlert | null {
  if (!isValidNumber(jsonSummaryAdvancedAlert.recid, { isRequiredToBeInteger: true })) {
    window.sc3WorkbenchLogger.warn(`Failed to parse advanced alert - jsonSummaryAdvancedAlert.recid is not an integer: ${jsonSummaryAdvancedAlert.recid}.`, jsonSummaryAdvancedAlert);
    return null;
  }

  const recid = Number(jsonSummaryAdvancedAlert.recid);
  const name = jsonSummaryAdvancedAlert.alert.trim();

  if (!name) {
    window.sc3WorkbenchLogger.warn(`Failed to parse advanced alert with recid: ${recid} - trimmed jsonSummaryAdvancedAlert.alert is an empty string.`, jsonSummaryAdvancedAlert);
    return null;
  }

  const lastFiredDateString = jsonSummaryAdvancedAlert.lastfired ?? '';
  const lastFiredDateObject = lastFiredDateString ? parseJsonSummaryAdvancedAlertOrPriceAlertDate(lastFiredDateString) : null;

  if (lastFiredDateString && !lastFiredDateObject) {
    window.sc3WorkbenchLogger.warn(`Failed to parse advanced alert with recid: ${recid} - unable to parse date from the jsonSummaryAdvancedAlert.lastfired string: ${jsonSummaryAdvancedAlert.lastfired}.`, jsonSummaryAdvancedAlert);
    return null;
  }

  return {
    name,
    lastFiredDateString,
    recid,
    isPaused: jsonSummaryAdvancedAlert.alertpaused === 'yes',
    status: jsonSummaryAdvancedAlert.alertpaused === 'yes' ? 'Paused' : 'Running',
    notes: jsonSummaryAdvancedAlert.notes?.trim() ?? '',
    lastFiredDateObject
  };
}

function parsePriceAlert(jsonSummaryPriceAlert: JsonSummaryPriceAlert): PriceAlert | null {
  if (!isValidNumber(jsonSummaryPriceAlert.recid, { isRequiredToBeInteger: true })) {
    window.sc3WorkbenchLogger.warn(`Failed to parse price alert - jsonSummaryPriceAlert.recid is not an integer: ${jsonSummaryPriceAlert.recid}.`, jsonSummaryPriceAlert);
    return null;
  }

  const recid = Number(jsonSummaryPriceAlert.recid);
  const name = jsonSummaryPriceAlert.alert.trim();

  if (!name) {
    window.sc3WorkbenchLogger.warn(`Failed to parse price alert with recid: ${recid} - trimmed jsonSummaryPriceAlert.alert is an empty string.`, jsonSummaryPriceAlert);
    return null;
  }

  // 'AAPL Crosses Above 218.76' -> ['AAPL', 'Crosses', 'Above', '218.76']
  const alertParts = jsonSummaryPriceAlert.alert.split(/\s/).filter(namePart => !!namePart);
  const alertPrice = alertParts.at(-1)?.replace(/,/g, '');

  if (!alertPrice || !isValidNumber(alertPrice)) {
    window.sc3WorkbenchLogger.warn(`Failed to parse price alert with recid: ${recid} - unable to determine if price alert alertPrice via parsing the jsonSummaryPriceAlert.alert string: ${jsonSummaryPriceAlert.alert}.`, jsonSummaryPriceAlert);
    return null;
  }

  const price = jsonSummaryPriceAlert.price.trim().replace(/,/g, '');

  if (!isValidNumber(price)) {
    window.sc3WorkbenchLogger.warn(`Failed to parse price alert with recid: ${recid} - trimmed jsonSummaryPriceAlert.price is not a valid number: ${jsonSummaryPriceAlert.price}.`, jsonSummaryPriceAlert);
    return null;
  }

  const symbol = jsonSummaryPriceAlert.symbol.trim().toUpperCase();

  if (!symbol) {
    window.sc3WorkbenchLogger.warn(`Failed to parse price alert with recid: ${recid} - trimmed jsonSummaryPriceAlert.symbol is an empty string.`, jsonSummaryPriceAlert);
    return null;
  }

  const lastFiredDateString = jsonSummaryPriceAlert.lastfired ?? '';
  const lastFiredDateObject = lastFiredDateString ? parseJsonSummaryAdvancedAlertOrPriceAlertDate(lastFiredDateString) : null;

  if (lastFiredDateString && !lastFiredDateObject) {
    window.sc3WorkbenchLogger.warn(`Failed to parse price alert with recid: ${recid} - unable to parse date from the jsonSummaryPriceAlert.lastfired string: ${jsonSummaryPriceAlert.lastfired}.`, jsonSummaryPriceAlert);
    return null;
  }

  return {
    name,
    lastFiredDateString,
    recid,
    symbol,
    isPaused: jsonSummaryPriceAlert.alertpaused === 'yes',
    status: jsonSummaryPriceAlert.alertpaused === 'yes' ? 'Paused' : 'Running',
    notes: jsonSummaryPriceAlert.notes?.trim() ?? '',
    lastFiredDateObject,
    price: Number(price).toFixed(2),
    delta: (Number(alertPrice) - Number(price)).toFixed(2)
  };
}

function parsePredefinedAlert(jsonLayerPredefinedAlert: JsonLayerPredefinedAlert): PredefinedAlert | null {
  if (!isValidNumber(jsonLayerPredefinedAlert.recID, { isRequiredToBeInteger: true })) {
    window.sc3WorkbenchLogger.warn(`Failed to parse predefined alert - jsonLayerPredefinedAlert.recID is not an integer: ${jsonLayerPredefinedAlert.recID}.`, jsonLayerPredefinedAlert);
    return null;
  }

  const recid = jsonLayerPredefinedAlert.recID;
  const name = jsonLayerPredefinedAlert.alertName.trim();

  if (!name) {
    window.sc3WorkbenchLogger.warn(`Failed to parse predefined alert with recid: ${recid} - trimmed jsonLayerPredefinedAlert.alertName is an empty string.`, jsonLayerPredefinedAlert);
    return null;
  }

  const symbol = jsonLayerPredefinedAlert.symbol.trim().toUpperCase();

  if (!symbol) {
    window.sc3WorkbenchLogger.warn(`Failed to parse predefined alert with recid: ${recid} - trimmed jsonLayerPredefinedAlert.symbol is an empty string.`, jsonLayerPredefinedAlert);
    return null;
  }

  const lastAlertDateString = jsonLayerPredefinedAlert.lastAlert.trim();
  const lastFiredDateObject = new Date(lastAlertDateString);

  if (!lastAlertDateString || lastFiredDateObject.toString() === 'Invalid Date') {
    window.sc3WorkbenchLogger.warn(`Failed to parse predefined alert with recid: ${recid} - trimmed jsonLayerPredefinedAlert.lastAlert is not a valid JavaScript date string: ${lastAlertDateString}.`, jsonLayerPredefinedAlert);
    return null;
  }

  return {
    name,
    lastFiredDateString: formatAlertDateString(lastFiredDateObject),
    recid,
    symbol,
    isBearish: jsonLayerPredefinedAlert.isBearish
  };
}

async function updatePriceAlertStatus(recid: number, isRunning: boolean): Promise<void> {
  const { chartCode } = useAuthentication();

  const chartCodeCopy = chartCode.value;
  let jsonLayerResponse;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: 'update-pricealert',
        chartCode: chartCodeCopy,
        recid: recid.toString(),
        isRunning: isRunning.toString()
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    jsonLayerResponse = await response.json();
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to update price alert status for recid: ${recid} and isRunning: ${isRunning} and chartCode: ${chartCodeCopy}.`, e);

    throw new Error(`Failed to update price alert status for recid: ${recid} and isRunning: ${isRunning} and chartCode: ${chartCodeCopy}.`, { cause: e });
  }

  if (!isJsonLayerResponse(jsonLayerResponse)) {
    throw new Error(`Failed to update price alert status for recid: ${recid} and isRunning: ${isRunning} and chartCode: ${chartCodeCopy} - jsonLayerResponse does not match JsonLayerResponse interface.`);
  }

  if (!jsonLayerResponse.success) {
    throw new Error(`Failed to update price alert status for recid: ${recid} and isRunning: ${isRunning} and chartCode: ${chartCodeCopy} - jsonLayerResponse.success is false - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }
}

async function pausePriceAlert(recid: number): Promise<void> {
  await updatePriceAlertStatus(recid, false);
}

async function startPriceAlert(recid: number): Promise<void> {
  await updatePriceAlertStatus(recid, true);
}

async function deletePriceAlert(recid: number): Promise<void> {
  const { chartCode } = useAuthentication();

  const chartCodeCopy = chartCode.value;
  let jsonLayerResponse;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: 'delete-pricealert',
        chartCode: chartCodeCopy,
        recid: recid.toString()
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    jsonLayerResponse = await response.json();
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to delete price alert for recid: ${recid} and chartCode: ${chartCodeCopy}.`, e);

    throw new Error(`Failed to delete price alert for recid: ${recid} and chartCode: ${chartCodeCopy}.`, { cause: e });
  }

  if (!isJsonLayerResponse(jsonLayerResponse)) {
    throw new Error(`Failed to delete price alert for recid: ${recid} and chartCode: ${chartCodeCopy} - jsonLayerResponse does not match JsonLayerResponse interface.`);
  }

  if (!jsonLayerResponse.success) {
    throw new Error(`Failed to delete price alert for recid: ${recid} and chartCode: ${chartCodeCopy} - jsonLayerResponse.success is false - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }
}

async function updateAdvancedAlertStatus(recid: number, isPaused: boolean): Promise<void> {
  const { chartCode } = useAuthentication();

  const chartCodeCopy = chartCode.value;
  let jsonLayerResponse;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: 'update-alert',
        chartCode: chartCodeCopy,
        recid: recid.toString(),
        alertType: isPaused ? '2' : '4'
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    jsonLayerResponse = await response.json();
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to update advanced alert status for recid: ${recid} and isPaused: ${isPaused} and chartCode: ${chartCodeCopy}.`, e);

    throw new Error(`Failed to update advanced alert status for recid: ${recid} and isPaused: ${isPaused} and chartCode: ${chartCodeCopy}.`, { cause: e });
  }

  if (!isJsonLayerResponse(jsonLayerResponse)) {
    throw new Error(`Failed to update advanced alert status for recid: ${recid} and isPaused: ${isPaused} and chartCode: ${chartCodeCopy} - jsonLayerResponse does not match JsonLayerResponse interface.`);
  }

  if (!jsonLayerResponse.success) {
    throw new Error(`Failed to update advanced alert status for recid: ${recid} and isPaused: ${isPaused} and chartCode: ${chartCodeCopy} - jsonLayerResponse.success is false - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }
}

async function pauseAdvancedAlert(recid: number): Promise<void> {
  await updateAdvancedAlertStatus(recid, true);
}

async function startAdvancedAlert(recid: number): Promise<void> {
  await updateAdvancedAlertStatus(recid, false);
}

async function deleteAdvancedAlert(recid: number): Promise<void> {
  const { chartCode } = useAuthentication();

  const chartCodeCopy = chartCode.value;
  let jsonLayerResponse;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: 'delete-alert',
        chartCode: chartCodeCopy,
        recid: recid.toString()
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    jsonLayerResponse = await response.json();
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to delete advanced alert for recid: ${recid} and chartCode: ${chartCodeCopy}.`, e);

    throw new Error(`Failed to delete advanced alert for recid: ${recid} and chartCode: ${chartCodeCopy}.`, { cause: e });
  }

  if (!isJsonLayerResponse(jsonLayerResponse)) {
    throw new Error(`Failed to delete advanced alert for recid: ${recid} and chartCode: ${chartCodeCopy} - jsonLayerResponse does not match JsonLayerResponse interface.`);
  }

  if (!jsonLayerResponse.success) {
    throw new Error(`Failed to delete advanced alert for recid: ${recid} and chartCode: ${chartCodeCopy} - jsonLayerResponse.success is false - jsonLayerResponse.message: ${jsonLayerResponse.message ?? '(message not provided)'}.`);
  }
}

function refreshAlerts(): void {
  if (
    (isAdvancedAlertsModeOpen?.value || isPriceAlertsModeOpen?.value) &&
    Date.now() - lastAdvancedAlertAndPriceAlertRefreshTime > REFRESH_INTERVAL
  ) {
    loadAdvancedAlertsAndPriceAlerts();
  }

  if (
    isPredefinedAlertsModeOpen?.value &&
    Date.now() - lastPredefinedAlertRefreshTime > REFRESH_INTERVAL
  ) {
    loadPredefinedAlerts();
  }
}

function init(): void {
  const { PREDEFINED_ALERTS_MODE, PRICE_ALERTS_MODE, ADVANCED_ALERTS_MODE, currentModeId } = useModes();

  if (isInitialized) {
    return;
  }

  isPredefinedAlertsModeOpen = isPredefinedAlertsModeOpen ?? computed(() => {
    return currentModeId.value === PREDEFINED_ALERTS_MODE.id;
  });

  isPriceAlertsModeOpen = isPriceAlertsModeOpen ?? computed(() => {
    return currentModeId.value === PRICE_ALERTS_MODE.id;
  });

  isAdvancedAlertsModeOpen = isAdvancedAlertsModeOpen ?? computed(() => {
    return currentModeId.value === ADVANCED_ALERTS_MODE.id;
  });

  watch(isPredefinedAlertsModeOpen, refreshAlerts);
  watch(isPriceAlertsModeOpen, refreshAlerts);
  watch(isAdvancedAlertsModeOpen, refreshAlerts);

  window.setInterval(refreshAlerts, CHECK_INTERVAL);

  isInitialized = true;
}

export function useAlerts() {
  const { addMemberAuthenticationCallback } = useAuthentication();

  addMemberAuthenticationCallback(init);

  return {
    advancedAlerts: readonly(advancedAlerts),
    priceAlerts: readonly(priceAlerts),
    predefinedAlerts: readonly(predefinedAlerts),
    advancedAlertsAndPriceAlertsLoadingState: readonly(advancedAlertsAndPriceAlertsLoadingState),
    predefinedAlertsLoadingState: readonly(predefinedAlertsLoadingState),
    currentNumberOfPriceAlerts,
    hasReachedMaximumNumberOfPriceAlerts,
    createPriceAlert,
    loadAdvancedAlertsAndPriceAlerts,
    deleteAdvancedAlert,
    deletePriceAlert,
    pauseAdvancedAlert,
    pausePriceAlert,
    startAdvancedAlert,
    startPriceAlert
  };
}
